/**
 * HelpTooltips - Reusable help tooltip components for view concepts
 *
 * Provides contextual guidance for View-to-View JOINs, dependencies,
 * performance, and other complex view concepts.
 */

import React from 'react';
import { HelpCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * Base help icon button component
 */
interface HelpIconProps {
  children: React.ReactNode;
  className?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
}

export const HelpIcon: React.FC<HelpIconProps> = ({ children, className = '', side = 'top' }) => {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={`inline-flex items-center justify-center rounded-full p-0.5 text-muted-foreground hover:text-foreground transition-colors ${className}`}
            aria-label="Help information"
          >
            <HelpCircle className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side={side} className="max-w-xs">
          {children}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * View-to-View JOINs help tooltip
 */
export const ViewJoinHelp: React.FC = () => (
  <HelpIcon>
    <div className="space-y-2">
      <p className="font-semibold">View-to-View JOINs</p>
      <p className="text-xs text-muted-foreground">
        Join this view against another view instead of a dataset. This is useful for creating
        reusable dimension views that can be shared across multiple views.
      </p>
      <div className="pt-1 border-t">
        <p className="text-xs font-medium mb-1">Example:</p>
        <p className="text-xs text-muted-foreground">
          Create a &quot;customer_dim&quot; view, then JOIN it to &quot;orders&quot; and &quot;returns&quot; views.
        </p>
      </div>
    </div>
  </HelpIcon>
);

/**
 * Dataset JOINs help tooltip
 */
export const DatasetJoinHelp: React.FC = () => (
  <HelpIcon>
    <div className="space-y-2">
      <p className="font-semibold">Dataset JOINs</p>
      <p className="text-xs text-muted-foreground">
        Join this view directly against a dataset&apos;s Parquet files. Use this when you want to
        combine raw data without creating intermediate views.
      </p>
      <div className="pt-1 border-t">
        <p className="text-xs font-medium mb-1">When to use:</p>
        <p className="text-xs text-muted-foreground">
          Simple lookups, one-time transformations, or when the target data won&apos;t be reused.
        </p>
      </div>
    </div>
  </HelpIcon>
);

/**
 * Chain depth help tooltip
 */
export const ChainDepthHelp: React.FC = () => (
  <HelpIcon>
    <div className="space-y-2">
      <p className="font-semibold">Dependency Chain Depth</p>
      <p className="text-xs text-muted-foreground">
        How many levels of views this view depends on. A depth of 0 means this view only uses
        datasets. A depth of 2 means it JOINs to views that themselves JOIN to other views.
      </p>
      <div className="pt-1 border-t space-y-1">
        <p className="text-xs">
          <span className="font-medium text-success">Depth 0-1:</span>
          <span className="text-muted-foreground ml-1">Fast queries</span>
        </p>
        <p className="text-xs">
          <span className="font-medium text-warning">Depth 2-3:</span>
          <span className="text-muted-foreground ml-1">May be slower</span>
        </p>
        <p className="text-xs">
          <span className="font-medium text-destructive">Depth 4+:</span>
          <span className="text-muted-foreground ml-1">Consider materializing</span>
        </p>
      </div>
    </div>
  </HelpIcon>
);

/**
 * Dependency graph help tooltip
 */
export const DependencyHelp: React.FC = () => (
  <HelpIcon>
    <div className="space-y-2">
      <p className="font-semibold">View Dependencies</p>
      <p className="text-xs text-muted-foreground">
        Shows the relationship between views. Upstream dependencies are views this view needs.
        Downstream dependents are views that need this view.
      </p>
      <div className="pt-1 border-t space-y-1">
        <p className="text-xs">
          <span className="font-medium">Upstream:</span>
          <span className="text-muted-foreground ml-1">Views this depends on</span>
        </p>
        <p className="text-xs">
          <span className="font-medium">Downstream:</span>
          <span className="text-muted-foreground ml-1">Views that depend on this</span>
        </p>
      </div>
    </div>
  </HelpIcon>
);

/**
 * Performance complexity help tooltip
 */
export const PerformanceHelp: React.FC = () => (
  <HelpIcon>
    <div className="space-y-2">
      <p className="font-semibold">Query Complexity</p>
      <p className="text-xs text-muted-foreground">
        Indicates how complex your view configuration is and the potential impact on query
        performance. Based on dependency depth and number of JOINs.
      </p>
      <div className="pt-1 border-t space-y-1">
        <p className="text-xs">
          <span className="font-medium text-success">Low:</span>
          <span className="text-muted-foreground ml-1">Fast, simple queries</span>
        </p>
        <p className="text-xs">
          <span className="font-medium text-warning">Medium:</span>
          <span className="text-muted-foreground ml-1">Good for most cases</span>
        </p>
        <p className="text-xs">
          <span className="font-medium text-alert">High:</span>
          <span className="text-muted-foreground ml-1">May need optimization</span>
        </p>
        <p className="text-xs">
          <span className="font-medium text-destructive">Very High:</span>
          <span className="text-muted-foreground ml-1">Materialize recommended</span>
        </p>
      </div>
    </div>
  </HelpIcon>
);

/**
 * Materialization help tooltip
 */
export const MaterializationHelp: React.FC = () => (
  <HelpIcon>
    <div className="space-y-2">
      <p className="font-semibold">Materialized Views</p>
      <p className="text-xs text-muted-foreground">
        Pre-compute and store query results as Parquet files for faster access. Recommended for
        complex views or frequently queried data.
      </p>
      <div className="pt-1 border-t space-y-1">
        <p className="text-xs font-medium mb-1">Trade-offs:</p>
        <p className="text-xs text-muted-foreground">
          ✓ Much faster queries
        </p>
        <p className="text-xs text-muted-foreground">
          ✗ Uses additional storage
        </p>
        <p className="text-xs text-muted-foreground">
          ✗ Requires periodic refreshes
        </p>
      </div>
    </div>
  </HelpIcon>
);

/**
 * JOIN type help tooltip
 */
export const JoinTypeHelp: React.FC = () => (
  <HelpIcon>
    <div className="space-y-2">
      <p className="font-semibold">JOIN Types</p>
      <div className="space-y-1.5 text-xs">
        <div>
          <p className="font-medium">INNER JOIN</p>
          <p className="text-muted-foreground">Only matching rows from both sides</p>
        </div>
        <div>
          <p className="font-medium">LEFT JOIN</p>
          <p className="text-muted-foreground">All rows from left, matching from right</p>
        </div>
        <div>
          <p className="font-medium">RIGHT JOIN</p>
          <p className="text-muted-foreground">All rows from right, matching from left</p>
        </div>
        <div>
          <p className="font-medium">FULL OUTER JOIN</p>
          <p className="text-muted-foreground">All rows from both sides</p>
        </div>
      </div>
    </div>
  </HelpIcon>
);

/**
 * Refresh schedule help tooltip
 */
export const RefreshScheduleHelp: React.FC = () => (
  <HelpIcon>
    <div className="space-y-2">
      <p className="font-semibold">Auto-Refresh Schedule</p>
      <p className="text-xs text-muted-foreground">
        Automatically update the view when new data arrives or on a time-based schedule.
      </p>
      <div className="pt-1 border-t space-y-1">
        <p className="text-xs">
          <span className="font-medium">On Ingestion:</span>
          <span className="text-muted-foreground ml-1">Refresh when files added</span>
        </p>
        <p className="text-xs">
          <span className="font-medium">Hourly/Daily/Weekly:</span>
          <span className="text-muted-foreground ml-1">Time-based schedule</span>
        </p>
        <p className="text-xs">
          <span className="font-medium">Manual:</span>
          <span className="text-muted-foreground ml-1">Refresh on demand only</span>
        </p>
      </div>
    </div>
  </HelpIcon>
);

/**
 * Column transformation help tooltip
 */
export const TransformationHelp: React.FC = () => (
  <HelpIcon>
    <div className="space-y-2">
      <p className="font-semibold">Column Transformations</p>
      <p className="text-xs text-muted-foreground">
        Apply SQL expressions to transform column values. Use DuckDB SQL functions for
        calculations, type conversions, and string manipulation.
      </p>
      <div className="pt-1 border-t">
        <p className="text-xs font-medium mb-1">Examples:</p>
        <code className="text-xs bg-muted px-1 py-0.5 rounded block mb-1">
          UPPER(name)
        </code>
        <code className="text-xs bg-muted px-1 py-0.5 rounded block mb-1">
          price * 1.1
        </code>
        <code className="text-xs bg-muted px-1 py-0.5 rounded block">
          CAST(amount AS INTEGER)
        </code>
      </div>
    </div>
  </HelpIcon>
);

/**
 * Circular dependency help tooltip
 */
export const CircularDependencyHelp: React.FC = () => (
  <HelpIcon side="right">
    <div className="space-y-2">
      <p className="font-semibold">Circular Dependencies</p>
      <p className="text-xs text-muted-foreground">
        Occur when View A depends on View B, and View B depends on View A (directly or through
        other views). This creates an infinite loop and is not allowed.
      </p>
      <div className="pt-1 border-t">
        <p className="text-xs font-medium mb-1">Prevention:</p>
        <p className="text-xs text-muted-foreground">
          The system automatically prevents selecting views that would create circular dependencies.
        </p>
      </div>
    </div>
  </HelpIcon>
);
